import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useFetcher, useSearchParams } from "@remix-run/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    AlertTriangle,
    CheckCircle,
    ImagePlus,
    Loader2,
    Sparkles,
    Trash2,
} from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { ETHNICITY_OPTIONS } from "~/components/settings/part-image-preferences-options";
import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { P } from "~/components/ui/typography";
import type {
    PartImageEthnicity,
    TPart,
    TPartImage,
    TPartInteraction,
} from "~/database/types";
import { useDeletePartImage } from "~/hooks/mutations/use-delete-part-image";
import { useGeneratePartImage } from "~/hooks/mutations/use-generate-part-image";
import { useSetDefaultPartImage } from "~/hooks/mutations/use-set-default-part-image";
import {
    usePartImagePreferences,
    useUpdatePartImagePreferences,
} from "~/hooks/queries/use-part-image-preferences";
import { usePartImageStatus } from "~/hooks/queries/use-part-image-status";
import { useApiClient } from "~/hooks/use-api-client";
import { useSession } from "~/hooks/use-session";
import { cn } from "~/lib/utils";

import { PartImage } from "./part-image";
import { PartImageGallery } from "./part-image-gallery";

// PartImageEditorModal Behavior:
//
// Initial Load:
// 1. When the modal opens, if the image list is loading, a loader covers the entire content area.
// 2. Once loaded, if a `defaultImageId` is set for the part, that image is selected.
// 3. If no `defaultImageId` is set, the first image in the gallery is selected.
// 4. If no images exist, an appropriate message is displayed.
//
// Image Generation:
// 1. On initiating generation, the main display area shows a generating state. `selectedImageId` is
//    set to the ID of the new image being generated.
// 2. The newly generated image (or its error state, if failed) automatically becomes selected
//    upon completion, taking precedence over any intermediate user clicks on other images.
// 3. If generation fails, the modal displays a failed image placeholder for that attempt,
//    and it remains selected.
//
// Image Deletion:
// 1. Upon successful deletion of an image, the `selectedImageId` is updated to the part's
//    new default image ID (as returned by the backend). If no default remains,
//    `selectedImageId` might become null.
//
// Selection & Display:
// - The main large image display always reflects the `selectedImageId`.
// - Polling updates the status/content of the `selectedImageId`.

// Define the schema from the DB if possible, otherwise list explicitly
const partImageStyles = [
    "DEFAULT",
    "ILLUSTRATION",
    "ABSTRACT",
    "ARCHETYPE",
    "SOMATIC",
    "PHOTOREALISTIC",
    "USER_PROVIDED",
    "TAROT",
] as const;

// Styles visible to users in the UI (excluding DEFAULT which is selected automatically)
// AIDEV-NOTE: PHOTOREALISTIC, ARCHETYPE, USER_PROVIDED hidden from UI; backend still supports them.
// USER_PROVIDED replaced by inline styleDirection field on any style.
const visiblePartImageStyles = [
    "ILLUSTRATION",
    "ABSTRACT",
    "SOMATIC",
    "TAROT",
] as const;

// AIDEV-NOTE: Keep preview metadata aligned with available part image styles & public assets.
const stylePreviewCards: ReadonlyArray<{
    value: (typeof visiblePartImageStyles)[number];
    label: string;
    imageSrc: string;
}> = visiblePartImageStyles.map((style) => {
    const sharedImageSrc = {
        ILLUSTRATION: "/style-illustrated.webp",
        ABSTRACT: "/style-abstract.webp",
        SOMATIC: "/style-somatic.webp",
        TAROT: "/style-tarot.webp",
    }[style];

    const label = {
        ILLUSTRATION: "Illustration",
        ABSTRACT: "Abstract",
        SOMATIC: "Somatic",
        TAROT: "Tarot",
    }[style];

    return {
        value: style,
        label,
        imageSrc: sharedImageSrc,
    };
});

interface PartImageWithSignedUrl extends TPartImage {
    signedUrl: string | null;
}

const formSchema = z.object({
    style: z.enum(partImageStyles, { required_error: "Style is required" }),
    prompt: z.string().max(1000).optional(),
});

type FormData = z.infer<typeof formSchema>;

type TPartWithPartInteractions = TPart & {
    part_interactions: TPartInteraction[];
};
interface PartImageEditorModalProps {
    part: TPartWithPartInteractions | null;
    partInteractionId?: string | null;
    isOpen: boolean;
    onClose: () => void;
    onImageGenerated?: () => void;
}

// Helper to safely get sessionId
const getSessionId = (part: TPartWithPartInteractions): string | undefined =>
    part.part_interactions?.[0]?.sessionId ?? undefined;

// AIDEV-NOTE: Get default image ID based on context:
// - If partInteractionId provided: use part_interaction's defaultImageId
// - Otherwise: use part's own defaultImageId (for parts detail page)
const getDefaultImageId = (
    part: TPartWithPartInteractions,
    partInteractionId?: string | null,
): string | null | undefined => {
    if (partInteractionId) {
        // Session context: find the specific part interaction
        const interaction = part.part_interactions?.find(
            (pi) => pi.id === partInteractionId,
        );
        return interaction?.defaultImageId;
    }
    // Parts page context: use the part's default image (TPart has defaultImageId)
    return part.defaultImageId;
};

export function PartImageEditorModal({
    part,
    partInteractionId,
    isOpen,
    onClose,
    onImageGenerated,
}: PartImageEditorModalProps) {
    const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
    const [formInitialized, setFormInitialized] = useState(false);
    const [searchParams] = useSearchParams();
    const sessionToken = searchParams.get("sessionToken");
    // AIDEV-NOTE: This modal is also reachable from the parts (non-session)
    // page where there is no sessionId, so isConsultantViewer is undefined
    // there — harmless, since a consultant grant is always session-scoped.
    const { isConsultantViewer } = useSession(
        (part && getSessionId(part)) || "",
    );
    // AIDEV-NOTE: Public session users cannot create/modify images. Self-
    // computed like TitleEditor/DateEditor — PartDetailPage already gates
    // whether this modal can even OPEN for a consultant, but this is the
    // same defense-in-depth as the rest of the part-detail subtree, and this
    // modal is also reached via other callers this audit didn't all trace
    // (Adam, 2026-09-03: "changing the image" must not be possible).
    const isPublicSession = !!sessionToken || isConsultantViewer;
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    // AIDEV-NOTE: Use fetcher for part detail page action (when no partInteractionId)
    const fetcher = useFetcher();

    // Fetch user's part image preferences
    const { data: userPreferences } = usePartImagePreferences();
    const { mutate: updatePreferences, isPending: isSavingEthnicity } =
        useUpdatePartImagePreferences();

    // AIDEV-NOTE: Match generate-part-image.ts override; prefer_not_to_say is allowed.
    const [pendingEthnicity, setPendingEthnicity] = useState<
        PartImageEthnicity | ""
    >("");
    const isRaceSafeAbstractRequired =
        !!userPreferences && !userPreferences.ethnicity;

    const handleSaveEthnicity = () => {
        if (!userPreferences || !pendingEthnicity) return;
        updatePreferences(
            { ...userPreferences, ethnicity: pendingEthnicity },
            {
                onSuccess: () => {
                    toast.success(
                        "Ethnicity saved — future images will use your selected style.",
                    );
                    setPendingEthnicity("");
                },
                onError: () => {
                    toast.error("Failed to save. Please try again.");
                },
            },
        );
    };

    const { data: imageListData, isLoading: isLoadingList } = useQuery<
        PartImageWithSignedUrl[],
        Error
    >({
        queryKey: ["partImages", part?.id, partInteractionId],
        queryFn: async () => {
            if (!part?.id) return [];
            // AIDEV-NOTE: Pass partId and partInteractionId as query params
            const queryParams = new URLSearchParams();
            queryParams.append("partId", part.id);
            if (partInteractionId) {
                queryParams.append("partInteractionId", partInteractionId);
            }
            const response = await apiClient.post(
                `/part/image/list?${queryParams.toString()}`,
                {
                    body: {
                        partId: part.id,
                        partInteractionId: partInteractionId,
                    },
                },
            );
            if (!response.ok) throw new Error("Failed to fetch image list");
            return response.json();
        },
        enabled: !!part?.id && isOpen,
        // AIDEV-NOTE: Poll every 2 seconds to update image statuses in the gallery
        refetchInterval: (query) => {
            const data = query.state.data;
            if (!data) return false;

            // Keep polling if any image is not in a terminal state
            const hasGeneratingImages = data.some(
                (img) =>
                    img.status === "CREATED" || img.status === "IN_PROGRESS",
            );
            return hasGeneratingImages ? 2000 : false;
        },
        refetchIntervalInBackground: true,
    });

    const { mutateAsync: generateImage, isPending: isGenerating } =
        useGeneratePartImage({
            onSuccessCallback: (newImageId: string) => {
                if (newImageId) {
                    setSelectedImageId(newImageId);
                    // Call the callback to refetch page data
                    onImageGenerated?.();
                }
            },
            sessionToken,
        });

    const { mutate: setDefaultImage, isPending: isSettingDefault } =
        useSetDefaultPartImage(sessionToken);
    const { mutate: deleteImage, isPending: isDeleting } = useDeletePartImage();

    const { control, handleSubmit, reset, setValue } = useForm<FormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            style: "ILLUSTRATION",
            prompt: "",
        },
    });

    // Lock the style selector to ABSTRACT when the pipeline will force ABSTRACT.
    useEffect(() => {
        if (isRaceSafeAbstractRequired) {
            setValue("style", "ABSTRACT");
        }
    }, [isRaceSafeAbstractRequired, setValue]);

    // Handle modal opening and image selection
    useEffect(() => {
        if (isOpen && part && imageListData) {
            if (isGenerating) {
                return;
            }
            // Only auto-select if nothing is currently selected
            if (selectedImageId == null) {
                const currentPartDefaultImageId = getDefaultImageId(
                    part,
                    partInteractionId,
                );
                if (
                    currentPartDefaultImageId &&
                    imageListData.some(
                        (img) => img.id === currentPartDefaultImageId,
                    )
                ) {
                    setSelectedImageId(currentPartDefaultImageId);
                } else if (imageListData.length > 0) {
                    setSelectedImageId(imageListData[0].id);
                } else {
                    setSelectedImageId(null);
                }
            }
        }
    }, [
        isOpen,
        part,
        imageListData,
        isGenerating,
        selectedImageId,
        partInteractionId,
    ]);

    // Handle modal closing
    useEffect(() => {
        if (!isOpen) {
            setSelectedImageId(null);
            setFormInitialized(false);
        }
    }, [isOpen]);

    // AIDEV-NOTE: Form reset separated from image selection to ensure user preferences are applied correctly
    // Handle form reset when modal opens and user preferences are loaded - only once per modal open
    useEffect(() => {
        if (isOpen && !formInitialized) {
            const newStyle = isRaceSafeAbstractRequired
                ? "ABSTRACT"
                : userPreferences?.defaultStyle || "ILLUSTRATION";
            reset({
                style: newStyle,
                prompt: "",
            });
            setFormInitialized(true);
        }
    }, [
        isOpen,
        userPreferences?.defaultStyle,
        isRaceSafeAbstractRequired,
        reset,
        formInitialized,
    ]);

    const {
        data: selectedImageData,
        isLoading: isLoadingSelectedImageDetails,
    } = usePartImageStatus(
        partInteractionId || undefined,
        selectedImageId,
        !!selectedImageId,
        part?.part_interactions?.[0]?.sessionId ?? undefined,
        sessionToken,
        partInteractionId,
    );

    const onSubmit = async (data: FormData) => {
        if (!part?.id) return;

        // AIDEV-NOTE: Block image generation for public session viewers
        if (isPublicSession) {
            toast.error("You can only create images for your own parts.");
            return;
        }

        setSelectedImageId(null);
        const sessionId = getSessionId(part);
        try {
            const result = await generateImage({
                partId: part.id,
                partInteractionId,
                sessionId,
                style: isRaceSafeAbstractRequired ? "ABSTRACT" : data.style,
                prompt: data.prompt,
            });
            setSelectedImageId(result.newImage.id);
        } catch (error) {
            console.error("Failed to initiate image generation:", error);
        }
    };

    const handleSetDefault = () => {
        if (!part?.id || !selectedImageId) return;

        // AIDEV-NOTE: Block for public session viewers
        if (isPublicSession) {
            toast.error("You can only create images for your own parts.");
            return;
        }

        // AIDEV-NOTE: Use page action for part detail page, API for session page
        if (!partInteractionId) {
            // Part detail page: use the page's action which triggers automatic revalidation
            fetcher.submit(
                {
                    intent: "set-part-default-image",
                    imageId: selectedImageId,
                },
                { method: "POST" },
            );
        } else {
            // Session page: use the API endpoint via mutation
            const sessionId = getSessionId(part);
            setDefaultImage(
                {
                    partId: part.id,
                    partInteractionId,
                    imageId: selectedImageId,
                    ...(sessionId ? { sessionId } : {}),
                },
                {
                    onSuccess: () => {
                        // Call the callback to refresh part data on the page
                        onImageGenerated?.();
                    },
                },
            );
        }
    };

    const handleDelete = () => {
        if (!part?.id || !partInteractionId || !selectedImageId) return;

        // AIDEV-NOTE: Block for public session viewers
        if (isPublicSession) {
            toast.error("You can only create images for your own parts.");
            return;
        }

        deleteImage(
            { partId: partInteractionId, imageId: selectedImageId },
            {
                onSuccess: (data) => {
                    // AIDEV-NOTE: API may return newDefaultImageId alongside success
                    const newDefaultId = (
                        data as { success: boolean; newDefaultImageId?: string }
                    )?.newDefaultImageId;
                    setSelectedImageId(newDefaultId || null);

                    queryClient.invalidateQueries({
                        queryKey: ["part", part.id],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["partImages", part.id],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["partImageStatus", part.id, selectedImageId],
                    });
                    if (newDefaultId && newDefaultId !== selectedImageId) {
                        queryClient.invalidateQueries({
                            queryKey: [
                                "partImageStatus",
                                part.id,
                                newDefaultId,
                            ],
                        });
                    }
                },
                onError: () => {
                    // Handle error (e.g., toast notification)
                },
            },
        );
    };

    const displayedImage = useMemo(() => {
        if (selectedImageData) return selectedImageData;
        return imageListData?.find((img) => img.id === selectedImageId) || null;
    }, [selectedImageData, imageListData, selectedImageId]);
    // AIDEV-NOTE: PartImage has a fallback spinner; hide it under this modal's generation overlay.
    const isDisplayedImageGenerating =
        displayedImage?.status === "IN_PROGRESS" ||
        displayedImage?.status === "CREATED";

    const isLoading =
        isGenerating ||
        isSettingDefault ||
        fetcher.state !== "idle" ||
        isDeleting ||
        isLoadingList ||
        (!!selectedImageId && isLoadingSelectedImageDetails);

    if (!isOpen && !part) return null;

    if (isLoadingList && isOpen) {
        return (
            <Dialog
                open={isOpen}
                onOpenChange={onClose}
                analyticsId="part-image-editor-modal-loading"
            >
                <DialogContent className="flex min-h-[400px] items-center justify-center sm:max-w-[800px]">
                    <Loader2 className="text-muted-foreground size-12 animate-spin" />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={onClose}
            analyticsId="part-image-editor-modal"
        >
            <DialogContent className="sm:max-w-[800px]">
                <DialogHeader>
                    <DialogTitle>
                        {imageListData && imageListData.length > 0
                            ? `Generate more images for ${
                                  part?.name || "this part"
                              }`
                            : "Create your first part image"}
                    </DialogTitle>
                </DialogHeader>

                <div className="mb-0 rounded-lg bg-brand-cream-100">
                    <P className="mb-0 text-sm leading-relaxed text-gray-700">
                        {imageListData && imageListData.length > 0 ? (
                            <>
                                How else might {part?.name || "this part"} want
                                be seen? Perhaps a different aspect, mood, or
                                quality wants to emerge.
                            </>
                        ) : (
                            <>
                                Take a moment to connect with{" "}
                                {part?.name || "this part"}. Close your eyes if
                                it helps, and gently ask: "How would you like to
                                be seen?" Notice what comes up - perhaps a
                                color, a texture, a scene, or even just a
                                feeling. There's no wrong answer here. Trust
                                what emerges, and choose the style below that
                                feels most aligned with what this part is
                                sharing with you.
                            </>
                        )}
                    </P>
                </div>

                {isRaceSafeAbstractRequired && !isPublicSession && (
                    <div className="flex items-start gap-3 rounded-lg border border-stone-200 bg-stone-50 p-4">
                        <Sparkles className="mt-0.5 size-5 flex-shrink-0 text-stone-500" />
                        <div className="flex-1 space-y-3">
                            <div>
                                <P className="mb-1 text-sm font-medium text-stone-900">
                                    Pick an ethnicity to unlock all styles
                                </P>
                                <P className="mb-0 text-xs text-stone-600">
                                    Until you choose one, we'll keep images
                                    abstract so we're not making assumptions.
                                </P>
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row">
                                <Select
                                    value={pendingEthnicity}
                                    onValueChange={(value) =>
                                        setPendingEthnicity(
                                            value as PartImageEthnicity,
                                        )
                                    }
                                >
                                    <SelectTrigger className="w-full bg-white">
                                        <SelectValue placeholder="Select ethnicity" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ETHNICITY_OPTIONS.map((option) => (
                                            <SelectItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleSaveEthnicity}
                                    disabled={
                                        !pendingEthnicity || isSavingEthnicity
                                    }
                                >
                                    {isSavingEthnicity ? (
                                        <Loader2 className="mr-2 size-4 animate-spin" />
                                    ) : null}
                                    Save
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-6 py-4 md:grid-cols-2">
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="space-y-4"
                    >
                        <div className="space-y-2">
                            <Label>Style</Label>
                            <Controller
                                name="style"
                                control={control}
                                render={({ field }) => (
                                    <div className="grid grid-cols-2 gap-4">
                                        {stylePreviewCards.map((style) => {
                                            const isAbstract =
                                                style.value === "ABSTRACT";
                                            const isLockedOut =
                                                isRaceSafeAbstractRequired &&
                                                !isAbstract;
                                            const isSelected =
                                                isRaceSafeAbstractRequired
                                                    ? isAbstract
                                                    : field.value ===
                                                      style.value;
                                            return (
                                                <button
                                                    type="button"
                                                    key={style.value}
                                                    onClick={() =>
                                                        field.onChange(
                                                            style.value,
                                                        )
                                                    }
                                                    disabled={
                                                        isLoading || isLockedOut
                                                    }
                                                    className={cn(
                                                        "flex flex-col items-center rounded-lg border bg-white p-3 transition border-2 border-gray-100",
                                                        "hover:border-brand-terra-300 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                                        {
                                                            "border-brand-terra-600 border-2 ring-primary hover:border-brand-terra-500":
                                                                isSelected,
                                                            "cursor-not-allowed opacity-40 hover:border-gray-100 hover:shadow-none":
                                                                isLockedOut,
                                                        },
                                                    )}
                                                    aria-pressed={isSelected}
                                                >
                                                    <img
                                                        src={style.imageSrc}
                                                        alt={`${style.label} preview`}
                                                        className="size-16 rounded-md object-cover mb-2"
                                                    />
                                                    <span
                                                        className={cn(
                                                            "font-medium text-center w-full",
                                                            {
                                                                "text-brand-terra-700":
                                                                    isSelected,
                                                            },
                                                        )}
                                                    >
                                                        {style.label}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="prompt">
                                Art Direction{" "}
                                <span className="text-muted-foreground font-normal">
                                    (Optional)
                                </span>
                            </Label>
                            <Controller
                                name="prompt"
                                control={control}
                                render={({ field }) => (
                                    <Textarea
                                        id="prompt"
                                        placeholder="e.g., wearing a blue hat, in a forest setting"
                                        {...field}
                                        rows={3}
                                        disabled={isLoading}
                                    />
                                )}
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={isLoading || !part?.id || isPublicSession}
                            size="lg"
                            className="w-full"
                        >
                            {isGenerating ? (
                                <div className="flex items-center">
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    <span>Generating... (20-30 seconds)</span>
                                </div>
                            ) : (
                                <div className="flex items-center">
                                    <ImagePlus className="mr-2 size-4" />
                                    <span className="">Generate Image</span>
                                </div>
                            )}
                        </Button>
                    </form>

                    <div className="space-y-4">
                        <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-md border bg-white">
                            {isLoading && !displayedImage && (
                                <Loader2 className="text-muted-foreground size-8 animate-spin" />
                            )}
                            {displayedImage && !isDisplayedImageGenerating && (
                                <PartImage
                                    image={displayedImage}
                                    // eslint-disable-next-line tailwindcss/enforces-shorthand
                                    className="h-full w-full object-contain"
                                />
                            )}
                            {!isLoadingSelectedImageDetails &&
                                !displayedImage?.signedUrl &&
                                !displayedImage &&
                                !isLoading &&
                                !isGenerating && (
                                    <span className="text-muted-foreground">
                                        {imageListData &&
                                        imageListData.length > 0
                                            ? "Select an image"
                                            : "Generate an image to get started"}
                                    </span>
                                )}
                            {displayedImage && isDisplayedImageGenerating && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white">
                                    <Loader2 className="mb-2 size-6 animate-spin" />
                                    <span>Generating...</span>
                                    <div className=" mt-2 text-xs">
                                        (this will take 20-30 seconds)
                                    </div>
                                </div>
                            )}
                            {displayedImage?.status === "ERROR" && (
                                <div className="bg-destructive/20 text-destructive-foreground absolute inset-0 flex flex-col items-center justify-center">
                                    <AlertTriangle className="mb-2 size-6" />
                                    <span>Generation Failed</span>
                                </div>
                            )}
                        </div>

                        {/* AIDEV-NOTE: My Parts context (no partInteractionId) sets only
                            part.defaultImageId; session pages read each interaction's own
                            defaultImageId and won't change. Explain this so clients aren't
                            confused when a session keeps its original image (EVO-1820). */}
                        {!isPublicSession && !partInteractionId && (
                            <P className="mb-0 text-xs leading-relaxed text-muted-foreground">
                                This sets the main image for{" "}
                                {part?.name || "this part"}. Images already
                                shown on individual session pages won't change —
                                open a session and set its image there if you'd
                                like it to match.
                            </P>
                        )}

                        {/* AIDEV-NOTE: Hide editing buttons for public sessions */}
                        {!isPublicSession && (
                            <div className="flex justify-end space-x-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleSetDefault}
                                    disabled={
                                        isLoading ||
                                        !selectedImageId ||
                                        !part ||
                                        getDefaultImageId(
                                            part,
                                            partInteractionId,
                                        ) === selectedImageId ||
                                        displayedImage?.status !== "COMPLETED"
                                    }
                                    analyticsId="set-default-part-image"
                                    data-testid="set-default-part-image"
                                >
                                    {isSettingDefault ||
                                    fetcher.state !== "idle" ? (
                                        <Loader2 className="mr-2 size-4 animate-spin" />
                                    ) : (
                                        <CheckCircle className="mr-2 size-4" />
                                    )}
                                    Set as Default
                                </Button>
                                {/* AIDEV-NOTE: Only show delete button when partInteractionId is available */}
                                {partInteractionId && (
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={handleDelete}
                                        disabled={isLoading || !selectedImageId}
                                    >
                                        {isDeleting ? (
                                            <Loader2 className="mr-2 size-4 animate-spin" />
                                        ) : (
                                            <Trash2 className="mr-2 size-4" />
                                        )}
                                        Delete
                                    </Button>
                                )}
                            </div>
                        )}

                        <PartImageGallery
                            images={imageListData || []}
                            selectedImageId={selectedImageId}
                            onSelectImage={setSelectedImageId}
                        />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
